// @flow
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  Banner,
  Box,
  Stack,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import { getPropertyTransactionCodes } from '../../components/CreateTransaction/actions';
import RenewalOfferForm from './RenewalOfferForm/index.js';
import RenewalOfferFormSkeleton, {
  renewalOfferFormSkeletonHeight,
} from './RenewalOfferFormSkeleton';
import PreviousOffersSection from './PreviousOffersSection/index.js';
import RenewalOfferHeaderComponent from './RenewalOfferHeader/index.js';
import { RenewalOfferContext } from './context';
import { useGenerateRenewalOffer } from './hooks';
import useIntl from '../../hooks/useIntl';
import messages from './messages';

export { default as RenewalOfferHeader } from './RenewalOfferHeader';

export const GenerateRenewalOffer = ({
  match,
  location,
  history,
  propertyTransactionCodes,
  onGetPropertyTransactionCodes,
}) => {
  const residentId = match?.params?.residentId;
  const prev = new URLSearchParams(location?.search ?? '').get('prev');

  const hookData = useGenerateRenewalOffer({
    residentId,
    history,
    propertyTransactionCodes,
    onGetPropertyTransactionCodes,
  });
  const intl = useIntl();

  const contextValue = {
    ...hookData,
    unitNumber: hookData.unit?.number ?? '',
  };

  return (
    <RenewalOfferContext.Provider value={contextValue}>
      <Box sx={{ px: 2, py: 1 }} data-test="generate-renewal-offer">
        <RenewalOfferHeaderComponent
          name={hookData.residentName}
          unitNumber={hookData.unit?.number ?? ''}
          goBack={prev === 'unavailable' ? null : hookData.goBack}
        />
        <Stack direction="column" spacing={2}>
          {hookData.loadFailed ? (
            <Banner
              status="error"
              message={intl.formatMessage(messages.loadError)}
              sx={{ width: '100%' }}
            />
          ) : (
            <>
              {/*
                The expiration field's default and resolver depend on isPartnerEnabled, which the
                pricing query answers, and useForm reads defaultValues once. So the form mounts only
                after the first resident, terms, and pricing requests settle, and a later refetch
                never unmounts it. AsyncBox alone cannot hold a mount back: it only fades its
                children. A query that never runs (flag off, or no lease) is idle, not loading, so
                it does not block the form. The settle-after-mount effect in
                RenewalOfferForm/hooks.js is the backstop. The LaunchDarkly half of the gate lives
                in App/Routes/MainLayoutRoutes/PageContent.js.
              */}
              <AsyncBox
                loading={hookData.isFormGated}
                Skeleton={
                  <RenewalOfferFormSkeleton
                    withTransactions={hookData.showsTransactionRows}
                  />
                }
                sx={{
                  // Reserve the form's height only while it is not mounted, so the skeleton
                  // has room and the page below does not move when the form replaces it.
                  minHeight: hookData.isFormGated
                    ? renewalOfferFormSkeletonHeight({
                        withTransactions: hookData.showsTransactionRows,
                      })
                    : 0,
                }}
              >
                {hookData.isFormGated ? null : (
                  <RenewalOfferForm
                    onSubmit={hookData.handleSubmit}
                    termsDropDown={hookData.leaseTermOptions}
                  />
                )}
              </AsyncBox>
              <PreviousOffersSection />
            </>
          )}
        </Stack>
      </Box>
    </RenewalOfferContext.Provider>
  );
};

export default connect(
  (state) => ({
    propertyTransactionCodes:
      state.currentProperty?.transactionCodes?.codes ?? [],
  }),
  (dispatch) => ({
    onGetPropertyTransactionCodes: bindActionCreators(
      getPropertyTransactionCodes,
      dispatch,
    ),
  }),
)(GenerateRenewalOffer);
